{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2022                                      }
{            Email : info@tmssoftware.com                            }
{            Web : http://www.tmssoftware.com                        }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.SpeechRecognition;

interface

uses
  web,js, Classes, weblib.Controls, WEBLib.StdCtrls, WEBLib.WebCtrls,
  WEBLib.Graphics, SysUtils;

type
  TCommandEvent = procedure(Sender: TObject; Parameters: TStrings) of object;

  TCommand = class(TCollectionItem)
  private
    FValue: string;
    FOnCommand: TCommandEvent;
    FTag: integer;
    procedure SetOnCommand(AEvent: TCommandEvent);
    procedure SetValue(AValue: String);
  public
    procedure Assign(Source: TPersistent); override;
  published
    property Tag: integer read FTag write FTag;
    property Value: String read FValue write SetValue;
    property OnCommand: TCommandEvent read FOnCommand write SetOnCommand;
  end;

  TCommands = class(TOwnedCollection)
  private
    FOnChange: TNotifyEvent;
    function GetItem(Index: integer): TCommand; reintroduce;
    procedure SetItem(Index: integer; const Value: TCommand);
  protected
     procedure Changed; reintroduce;
     procedure Update(Item: TCollectionItem); override;
  public
    constructor Create(AOwner: TComponent);
    procedure EndUpdate; override;
    function Find(ACommand: string): TCommand;
    function Add: TCommand; reintroduce;
    procedure Delete(Index: Integer); reintroduce;
    function Insert(Index: integer): TCommand; reintroduce;
    property Items[Index: integer]: TCommand read GetItem write SetItem; default;
    property OnChange: TNotifyEvent read FOnChange write FOnChange;
  end;

  TPhrasesEvent = procedure(Sender: TObject; Phrases: TStrings) of object;
  TResultMatchEvent = procedure (Sender: TObject; userSaid: String; Parameters: TStrings; Command: TCommand; Phrases: TStrings) of object;

  TSpeechRecognition = class(TComponent)
  private
    FLanguage: String;
    FCommands: TCommands;
    FOnResultMatch: TResultMatchEvent;
    FOnNoResultMatch: TPhrasesEvent;
    FOnResult: TPhrasesEvent;
    FOnNetworkError: TNotifyEvent;
    FOnSoundStart: TNotifyEvent;
    FOnEnd: TNotifyEvent;
    FOnPermissionBlockedError: TNotifyEvent;
    FOnPermissionDeniedError: TNotifyEvent;
    FOnStart: TNotifyEvent;
    FOnError: TNotifyEvent;
    procedure SetLanguage(ALanguage: String);
    procedure SetCommands(ACommands: TCommands);
  protected
    procedure DoCommandsChanged(Sender: TObject);
  public
    procedure Start;
    procedure Abort;
    procedure Pause;
    procedure Resume;
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
  published
    property Language: String read FLanguage write SetLanguage;
    property Commands: TCommands read FCommands write SetCommands;
    property OnResultMatch: TResultMatchEvent read FOnResultMatch write FOnResultMatch;
    property OnNoResultMatch: TPhrasesEvent read FOnNoResultMatch write FOnNoResultMatch;
    property OnResult: TPhrasesEvent read FOnResult write FOnResult;
    property OnStart: TNotifyEvent read FOnStart write FOnStart;
    property OnEnd: TNotifyEvent read FOnEnd write FOnEnd;
    property OnSoundStart: TNotifyEvent read FOnSoundStart write FOnSoundStart;
    property OnError: TNotifyEvent read FOnError write FOnError;
    property OnNetworkError: TNotifyEvent read FOnNetworkError write FOnNetworkError;
    property OnPermissionBlockedError: TNotifyEvent read FOnPermissionBlockedError write FOnPermissionBlockedError;
    property OnPermissionDeniedError: TNotifyEvent read FOnPermissionDeniedError write FonPermissionDeniedError;
  end;

  TWebSpeechRecognition = class(TSpeechRecognition);

implementation

{TSpeechRecognition}

{$HINTS OFF}
constructor TSpeechRecognition.Create(AOwner: TComponent);

  procedure HandleCommand(ACommand: string; parameters: TJSArray; Phrase: String; alt: TJSArray);
  var
    c: TCommand;
    para, alternatives: TStrings;
    r: String;
    s: JSValue;
  begin
    r := StringReplace(ACommand, '(.)(!)(?)', '', [rfReplaceAll]);
    if Commands <> nil then
      c := FCommands.Find(ACommand);
    para := TStringList.Create;
    for s in parameters do
    begin
      r := StringReplace(string(s), '.', '', [rfReplaceAll]);
      para.add(r);
    end;

    alternatives := TStringList.Create;
     for s in alt do
     begin
      r := StringReplace(string(s), '.', '', [rfReplaceAll]);
      alternatives.add(r);
    end;

    if Assigned(c) and Assigned(c.OnCommand) then
      c.OnCommand(c, para);

    if Assigned(OnResultMatch) then
      OnResultMatch(Self, Phrase, para, c, alternatives);
  end;

  procedure HandleNoResultMatch(Phrases: TJSArray);
  var
    p: TStrings;
    r: string;
    s: JSValue;
  begin
    p := TStringList.Create;
    for s in Phrases do
    begin
      r := StringReplace(string(s), '.', '', [rfReplaceAll]);
      p.add(r);
    end;

    if Assigned(OnNoResultMatch) then
      OnNoResultMatch(Self, p);
  end;

  procedure HandleResult(Phrases: TJSArray);
  var
    p: TStrings;
    r: String;
    s: JSValue;
  begin
    p := TStringList.Create;
    for s in Phrases do
    begin
      r := StringReplace(string(s), '.', '', [rfReplaceAll]);
      p.add(r);
    end;
      
    if Assigned(OnResult) then
      OnResult(Self, p);
  end;

  procedure HandleStart;
  begin
    if Assigned(OnStart) then
      OnStart(Self);
  end;

  procedure HandleEnd;
  begin
    if Assigned(OnEnd) then
      OnEnd(Self);
  end;

  procedure HandleSoundStart;
  begin
    if Assigned(OnSoundStart) then
      OnSoundStart(Self);
  end;

  procedure HandleError;
  begin
    if Assigned(OnError) then
      OnError(Self);
  end;

  procedure HandleNetworkError;
  begin
    if Assigned(OnNetworkError) then
      OnNetworkError(Self);
  end;

  procedure HandlePermissionBlocked;
  begin
    if Assigned(OnPermissionBlockedError) then
      OnPermissionBlockedError(Self);
  end;

  procedure HandlePermissionDenied;
  begin
     if Assigned(OnPermissionDeniedError) then
      OnPermissionDeniedError(Self);
  end;

begin
  inherited;
  FLanguage := 'en-US';
  FCommands := TCommands.Create(Self);
  FCommands.OnChange := DoCommandsChanged;
  asm
    if (annyang){
      annyang.addCallback('resultMatch', function(parameters, phrase, cmd,alt)
      {
        HandleCommand(cmd, parameters, phrase, alt);
      });

      annyang.addCallback('resultNoMatch', function(phrases)
      {
        HandleNoResultMatch(phrases);
      });

      annyang.addCallback('start', function()
      {
        HandleStart();
      });

      annyang.addCallback('soundstart', function()
      {
        HandleSoundStart();
      });
      annyang.addCallback('error', function()
      {
        HandleError();
      });
      annyang.addCallback('errorNetwork', function()
      {
        HandleNetworkError();
      });
      annyang.addCallback('errorPermissionBlocked', function()
      {
        HandlePermissionBlocked();
      });
      annyang.addCallback('errorPermissionDenied', function()
      {
        HandlePermissionDenied();
      });
      annyang.addCallback('end', function()
      {
        HandleEnd();
      });
      annyang.addCallback('result', function(phrases)
      {
        HandleResult(phrases);
      });
    }
  end;
end;
{$HINTS ON}

destructor TSpeechRecognition.Destroy;
begin
  FCommands.free;
  inherited;
end;

{$HINTS OFF}
procedure TSpeechRecognition.DoCommandsChanged(Sender: TObject);
var
  cmd: string;
  event: TCommandEvent;
  i: Integer;
begin
  asm
    if (annyang) {
      annyang.removeCommands();
    }
  end;
  for i := 0 to FCommands.Count -1 do
  begin
    cmd := FCommands[i].Value + '(.)(!)(?)';
    asm
      if (annyang){
        var commands = {};
        commands[cmd] = function(){ return undefined;};
        annyang.addCommands(commands);
        }
    end;
  end;
end;
{$HINTS ON}

procedure TSpeechRecognition.Start;
begin
  asm
    annyang.start();
  end;
end;

procedure TSpeechRecognition.Abort;
begin
  asm
    annyang.abort();
  end;
end;

procedure TSpeechRecognition.Pause;
begin
  asm
    annyang.pause();
  end;
end;

procedure TSpeechRecognition.Resume;
begin
  asm
    annyang.resume();
  end;
end;

procedure TSpeechRecognition.SetLanguage(aLanguage: String);
begin
  if FLanguage <> aLanguage then
  begin
    FLanguage := aLanguage;
    asm
      if (annyang) {
        annyang.setLanguage(this.FLanguage);
      }
    end;
  end;
end;

procedure TSpeechRecognition.SetCommands(ACommands: TCommands);
begin
  FCommands.Assign(ACommands);
end;

{TCommands}

procedure TCommands.Changed;
begin
  if (UpdateCount = 0) And Assigned(OnChange) then
    OnChange(Self);
end;

constructor TCommands.Create(AOwner: TComponent);
begin
  inherited Create(AOwner, TCommand);
end;

procedure TCommands.Delete(Index: Integer);
begin
  Inherited Delete(Index);
  Changed;
end;

procedure TCommands.EndUpdate;
begin
  inherited;
  Changed;
end;

function TCommands.Find(ACommand: string): TCommand;
var
  i: integer;
begin
  Result := nil;
  for i := 0 to Count - 1 do
  begin
    if (CompareText(Items[i].Value + '(.)(!)(?)', ACommand) = 0) then
    begin
      Result := Items[i];
      break;
    end;
  end;
end;

function TCommands.Add: TCommand;
begin
  Result := TCommand(inherited Add);
  Changed;
end;

function TCommands.Insert(Index: integer): TCommand;
begin
  Result := TCommand(inherited Insert(Index));
  Changed;
end;

function TCommands.GetItem(Index: integer): TCommand;
begin
  Result := TCommand(inherited Items[Index]);
end;

procedure TCommands.SetItem(Index: integer; const Value: TCommand);
begin
  inherited Items[Index] := Value;
end;

procedure TCommands.Update(Item: TCollectionItem);
begin
  inherited;
  Changed;
end;

{TCommand}

procedure TCommand.SetOnCommand(AEvent: TCommandEvent);
begin
  FOnCommand := AEvent;
  (Collection as TCommands).Update(Self); 
end;

procedure TCommand.SetValue(AValue: String);
begin
  if FValue <> AValue then
  begin
    FValue := AValue;
  end;
end;

procedure TCommand.Assign(Source: TPersistent);
begin
  if (Source is TCommand) then
  begin
    FValue := (Source as TCommand).Value;
    FTag := (Source as TCommand).Tag;
    FOnCommand := (Source as TCommand).OnCommand;
    (Collection as TCommands).Update(Self);
  end;
end;

end.